import { workspaceDetailResponseSchema } from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { z } from 'zod';

import { ApiRequestError, apiGet, apiPost, toApiError } from '@/lib/api';

/**
 * Cross-cutting UI state: toasts, the confirm dialog, the realtime connection
 * indicator and the workspace-wide emergency stop.
 *
 * `confirm()` returns a promise so a destructive action reads as a single
 * sequential step at the call site. Every destructive action in this app awaits
 * it — there is no "just do it" path.
 *
 * THE KILL SWITCH LIVES HERE, both the state and the two calls that flip it.
 * It could plausibly have gone in `stores/comments` — the endpoints hang off
 * `/comments` — but the flag is workspace-wide, it is read by screens that have
 * nothing to do with the comment queue (`post-actions`, the draft dialog, the
 * shell banner), and splitting "what the switch is" from "how you throw it"
 * across two stores is how one of them ends up stale. One fact, one owner.
 */

/**
 * The reply from both emergency-stop endpoints, mirrored rather than imported.
 *
 * Every other response in this app is parsed with a schema from
 * `@midiautolistening/contracts`, and this one is the exception because the DTO
 * was declared in `apps/api/src/comments/comments.service.ts` and never moved
 * into the contract package. A local mirror is the lesser of the two evils on
 * offer: the alternative is calling `request()` with no schema at all, which is
 * the single thing `lib/api` exists to prevent.
 *
 * Deliberately no stricter than the server. `changedAt` is a plain `z.string()`
 * there, so demanding `isoDateTimeSchema` here would turn a working kill switch
 * into CONTRACT_MISMATCH — on the one button that must never fail for a reason
 * the user cannot act on.
 *
 * When the DTO moves into contracts, delete this and import it.
 */
export const emergencyStopResponseSchema = z.object({
  active: z.boolean(),
  affectedProjectIds: z.array(z.string()),
  /** Projects that were RUNNING/STARTING and were pushed to PAUSED. */
  pausedProjectIds: z.array(z.string()),
  /** Comment jobs the switch marked CANCELLED. Terminal — clearing never revives them. */
  cancelledJobIds: z.array(z.string()),
  changedAt: z.string(),
});
export type EmergencyStopResponse = z.infer<typeof emergencyStopResponseSchema>;

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  message: string | null;
}

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

let nextToastId = 1;

export const useUiStore = defineStore('ui', () => {
  const toasts = ref<Toast[]>([]);
  const pendingConfirm = ref<PendingConfirm | null>(null);
  const realtimeStatus = ref<RealtimeStatus>('idle');
  const emergencyStopActive = ref(false);
  const emergencyStopReason = ref<string | null>(null);
  /** When the switch was thrown, ISO-8601. Null while it is off, or unknown. */
  const emergencyStopAt = ref<string | null>(null);
  /**
   * False until the switch has actually been read from the server once.
   *
   * `emergencyStopActive` used to start `false` and be written by nothing but a
   * socket event, so after a page reload the dashboard rendered "đang tắt" —
   * which is an assertion — for a workspace that was halted. `false` here means
   * "chưa đo", and it is a different fact from "đang tắt". Screens that want to
   * say something about the switch must consult this first.
   */
  const emergencyStopKnown = ref(false);
  /** A flip that failed. Never a read that failed — see `syncEmergencyStop`. */
  const emergencyStopError = ref<ApiRequestError | null>(null);
  const emergencyStopBusy = ref(false);
  /**
   * Bumped by every write to the four refs above. Not state anybody renders — it
   * exists so a slow read cannot land on top of a newer socket event.
   */
  let emergencyStopRevision = 0;

  function dismissToast(id: number): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id);
  }

  function pushToast(kind: ToastKind, title: string, message: string | null = null): number {
    const id = nextToastId++;
    toasts.value = [...toasts.value, { id, kind, title, message }];
    // Errors stay until dismissed; anything else is transient.
    if (kind !== 'error') {
      window.setTimeout(() => dismissToast(id), 4_500);
    }
    return id;
  }

  function notifySuccess(title: string, message?: string): void {
    pushToast('success', title, message ?? null);
  }

  function notifyInfo(title: string, message?: string): void {
    pushToast('info', title, message ?? null);
  }

  function notifyWarning(title: string, message?: string): void {
    pushToast('warning', title, message ?? null);
  }

  /**
   * Report a failed action. Contract mismatches are surfaced with their code so a
   * server that drifted is obvious rather than looking like a random glitch.
   */
  /**
   * Show a failure, INCLUDING which fields the server rejected.
   *
   * `details` was previously dropped, which turned every validation failure into
   * the same unactionable sentence — "Dữ liệu gửi lên không hợp lệ." — while the
   * server had already said exactly which field was wrong and why. The person
   * reading the toast is the one who can fix it, so they get the field names.
   */
  function notifyError(title: string, error: unknown): void {
    if (error instanceof ApiRequestError) {
      const suffix = error.looksLikeMissingEndpoint
        ? ' (endpoint có thể chưa được hiện thực trên máy chủ)'
        : '';
      const fields =
        error.details === null
          ? ''
          : Object.entries(error.details)
              .map(([field, messages]) => `\n• ${field}: ${messages.join('; ')}`)
              .join('');
      pushToast('error', title, `${error.code}: ${error.message}${suffix}${fields}`);
      return;
    }
    const message = error instanceof Error ? error.message : String(error);
    pushToast('error', title, message);
  }

  function confirm(options: ConfirmOptions): Promise<boolean> {
    // A second confirm while one is open would orphan the first promise.
    pendingConfirm.value?.resolve(false);

    return new Promise<boolean>((resolve) => {
      pendingConfirm.value = { ...options, resolve };
    });
  }

  function resolveConfirm(accepted: boolean): void {
    const pending = pendingConfirm.value;
    pendingConfirm.value = null;
    pending?.resolve(accepted);
  }

  function setRealtimeStatus(status: RealtimeStatus): void {
    realtimeStatus.value = status;
  }

  /**
   * Apply what the server said about the switch — from a socket event, or from
   * the response to our own flip.
   *
   * `changedAt` is optional only so a caller that has no timestamp cannot be
   * forced to invent one; passing nothing means "đang bật, không rõ từ bao giờ",
   * which the banner may render as such. The reason and the timestamp are both
   * dropped when the switch goes off: they describe an activation that is over,
   * and a "Lý do" line under a switch nobody is holding down is a leftover, not
   * information.
   */
  function setEmergencyStop(
    active: boolean,
    reason: string | null,
    changedAt: string | null = null,
  ): void {
    emergencyStopActive.value = active;
    emergencyStopReason.value = active ? reason : null;
    emergencyStopAt.value = active ? changedAt : null;
    emergencyStopKnown.value = true;
    emergencyStopRevision += 1;
  }

  /**
   * Read the switch from the server, because realtime alone cannot answer it.
   *
   * `GET /workspace` is the only endpoint that reports the workspace-wide state
   * without a project id in hand, and it answers `emergencyStopActive` plus
   * `emergencyStopAt` — the latter read from the `comment.emergency_stop` audit
   * row, so it is when the switch was *thrown*, not when some settings row was
   * last saved.
   *
   * IT CANNOT RESTORE THE REASON, and does not pretend to. The reason only ever
   * travels on the socket event, and the two timestamps involved (the event's
   * `changedAt`, taken before the writes; the audit row's `created_at`, written
   * after them) are close but never equal, so there is no way to prove that a
   * reason still in hand belongs to the activation this read describes. Carrying
   * it forward would eventually print somebody's old reason under a switch that
   * had been cleared and re-thrown while this tab was offline. A missing line
   * costs a reader one question; a wrong one costs them the incident.
   *
   * Failures are swallowed rather than toasted. This runs on every connect
   * attempt, including each backoff tick while the API is down, and a toast per
   * tick is a storm over a fact `emergencyStopKnown` already carries honestly.
   */
  async function syncEmergencyStop(): Promise<void> {
    const before = emergencyStopRevision;
    try {
      const detail = await apiGet('/workspace', workspaceDetailResponseSchema);
      /**
       * A socket event — or the operator's own flip — landed while this read was
       * in flight, which makes it newer than the row this response describes.
       * Without the check a sync begun before somebody pressed the switch could
       * answer "đang tắt" a moment after the event said "đang bật", and the one
       * direction that must never happen here is hiding a workspace that is
       * halted. The two timestamps cannot be compared (see above), so a local
       * revision counter is what decides.
       */
      if (emergencyStopRevision !== before) return;
      setEmergencyStop(detail.emergencyStopActive, null, detail.emergencyStopAt);
    } catch {
      // Leaves `emergencyStopKnown` where it was: a failed read must not be
      // allowed to look like a switch that is off.
    }
  }

  /**
   * Throw the kill switch for the whole workspace.
   *
   * `POST /comments/emergency-stop`, body `{ reason?: string }` (trimmed, ≤ 500).
   * Open to any authenticated member on purpose — a switch that asks whether you
   * are allowed to press it is not a kill switch.
   *
   * Rethrows so the caller can report the failure; `emergencyStopError` holds it
   * for a screen that would rather render than toast. The result is applied to
   * local state immediately instead of waiting for the `emergency_stop.changed`
   * broadcast to come back: this whole store used to learn the switch's state
   * from the socket and nowhere else, and a socket that is down is exactly the
   * situation in which somebody reaches for this button.
   *
   * NOT guarded against a second press while one is in flight. A duplicate stop
   * costs an extra audit row; a stop that refused to fire because the previous
   * one had not answered yet costs whatever the agent types in the meantime.
   */
  async function activateEmergencyStop(reason?: string): Promise<EmergencyStopResponse> {
    emergencyStopBusy.value = true;
    emergencyStopError.value = null;
    const trimmed = reason?.trim() ?? '';
    try {
      const response = await apiPost(
        '/comments/emergency-stop',
        emergencyStopResponseSchema,
        trimmed === '' ? {} : { reason: trimmed },
      );
      setEmergencyStop(response.active, trimmed === '' ? null : trimmed, response.changedAt);
      return response;
    } catch (caught) {
      const failure = toApiError(caught);
      emergencyStopError.value = failure;
      throw failure;
    } finally {
      emergencyStopBusy.value = false;
    }
  }

  /**
   * Release the switch. `POST /comments/emergency-stop/clear`, no body.
   *
   * Not symmetric with `activateEmergencyStop` and that asymmetry is the
   * server's: the clear route is `@Roles('ADMIN')`, so an ordinary
   * member who presses this gets 403 for a reason that has nothing to do with
   * the switch itself. `describeEmergencyStopFailure` in `lib/comment-view` is
   * where that becomes a sentence worth reading.
   */
  async function clearEmergencyStop(): Promise<EmergencyStopResponse> {
    emergencyStopBusy.value = true;
    emergencyStopError.value = null;
    try {
      const response = await apiPost('/comments/emergency-stop/clear', emergencyStopResponseSchema);
      setEmergencyStop(response.active, null, response.changedAt);
      return response;
    } catch (caught) {
      const failure = toApiError(caught);
      emergencyStopError.value = failure;
      throw failure;
    } finally {
      emergencyStopBusy.value = false;
    }
  }

  function reset(): void {
    toasts.value = [];
    resolveConfirm(false);
    realtimeStatus.value = 'idle';
    emergencyStopActive.value = false;
    emergencyStopReason.value = null;
    emergencyStopAt.value = null;
    // Back to "chưa đo", not to "đang tắt": a logout does not turn the switch off.
    emergencyStopKnown.value = false;
    emergencyStopError.value = null;
    emergencyStopBusy.value = false;
  }

  return {
    toasts,
    pendingConfirm,
    realtimeStatus,
    emergencyStopActive,
    emergencyStopReason,
    emergencyStopAt,
    emergencyStopKnown,
    emergencyStopError,
    emergencyStopBusy,
    pushToast,
    dismissToast,
    notifySuccess,
    notifyInfo,
    notifyWarning,
    notifyError,
    confirm,
    resolveConfirm,
    setRealtimeStatus,
    setEmergencyStop,
    syncEmergencyStop,
    activateEmergencyStop,
    clearEmergencyStop,
    reset,
  };
});
